import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { SwitchFormField } from '@repo/shared-client/components/fields'
import { Button, Card, CardContent, CardHeader, CardTitle, Separator, Spinner } from '@repo/shared-client/components/ui'
import { Form, useForm, useFormState, useWatch } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  ProfileEditEmailSettingsQuery,
  ProfileEditEmailSettingsQueryVariables,
  UpdateSettingsEmailInput,
  UpdateSettingsMutation,
  UpdateSettingsMutationVariables,
} from 'types/graphql'

const ACCOUNT_EDIT_EMAIL_SETTINGS_QUERY: TypedDocumentNode<
  ProfileEditEmailSettingsQuery,
  ProfileEditEmailSettingsQueryVariables
> = gql`
  query ProfileEditEmailSettingsQuery($profileId: String!) {
    settings: settingsEmail(profileId: $profileId) {
      id
      commentReply
      enableEmails
      enableMarketingEmails
      eventCancelled
      eventDetailsUpdated
      eventInvitation
      eventJoinRequestApproved
      eventAttendeeStatusChanged
      eventReminders
      membershipExpiryReminders
      friendApproved
      friendRequest
      groupInvitation
      groupJoinRequestApproved
      groupJoinRequestReceived
      groupPostRejected
      mediaComment
      messageNew
      ownerId
      placeReview
      postComment
      publicationReview
      vendorMarketItemReview
      vendorMarketReview
      reviewReply
    }
  }
`

const UPDATE_SETTINGS_MUTATION: TypedDocumentNode<UpdateSettingsMutation, UpdateSettingsMutationVariables> = gql`
  mutation UpdateSettings($profileId: String!, $input: UpdateSettingsEmailInput!) {
    updateSettingsEmail(profileId: $profileId, input: $input) {
      id
    }
  }
`

type EmailSettings = NonNullable<ProfileEditEmailSettingsQuery['settings']>

export const Route = createFileRoute('/(auth)/(account-layout)/account/$profileId/email')({
  component: EditEmailSettingsPage,
  head: () =>
    seo({
      title: `Email Settings | ${env.VITE_APP_NAME}`,
      description: 'Email Settings',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, ACCOUNT_EDIT_EMAIL_SETTINGS_QUERY, {
      variables: { profileId: params.profileId },
    })
    return { queryRef }
  },
})

function EditEmailSettingsPage() {
  const { t } = useTranslation()
  const { queryRef } = Route.useLoaderData()
  const apolloClient = useApolloClient()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<ProfileEditEmailSettingsQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading

  const handleRefetch = () => {
    return apolloClient.refetchQueries({ include: [ACCOUNT_EDIT_EMAIL_SETTINGS_QUERY] })
  }

  const [showSpinner, setShowSpinnerValue] = useState(false)

  const settings = data?.settings as EmailSettings | undefined

  const formMethods = useForm({
    defaultValues: {
      commentReply: settings?.commentReply ?? false,
      enableEmails: settings?.enableEmails ?? false,
      enableMarketingEmails: settings?.enableMarketingEmails ?? true,
      eventCancelled: settings?.eventCancelled ?? false,
      eventDetailsUpdated: settings?.eventDetailsUpdated ?? false,
      eventInvitation: settings?.eventInvitation ?? false,
      eventJoinRequestApproved: settings?.eventJoinRequestApproved ?? false,
      eventAttendeeStatusChanged: settings?.eventAttendeeStatusChanged ?? false,
      eventReminders: settings?.eventReminders ?? false,
      membershipExpiryReminders: settings?.membershipExpiryReminders ?? false,
      friendApproved: settings?.friendApproved ?? false,
      friendRequest: settings?.friendRequest ?? false,
      groupInvitation: settings?.groupInvitation ?? false,
      groupJoinRequestApproved: settings?.groupJoinRequestApproved ?? false,
      groupJoinRequestReceived: settings?.groupJoinRequestReceived ?? false,
      groupPostRejected: settings?.groupPostRejected ?? false,
      mediaComment: settings?.mediaComment ?? false,
      messageNew: settings?.messageNew ?? false,
      placeReview: settings?.placeReview ?? false,
      postComment: settings?.postComment ?? false,
      publicationReview: settings?.publicationReview ?? false,
      vendorMarketItemReview: settings?.vendorMarketItemReview ?? false,
      vendorMarketReview: settings?.vendorMarketReview ?? false,
      reviewReply: settings?.reviewReply ?? false,
    },
  })

  const { isDirty } = useFormState({ control: formMethods.control })

  useEffect(() => {
    if (settings) {
      formMethods.reset({
        commentReply: settings.commentReply,
        enableEmails: settings.enableEmails,
        enableMarketingEmails: settings.enableMarketingEmails,
        eventCancelled: settings.eventCancelled,
        eventDetailsUpdated: settings.eventDetailsUpdated,
        eventInvitation: settings.eventInvitation,
        eventJoinRequestApproved: settings.eventJoinRequestApproved,
        eventAttendeeStatusChanged: settings.eventAttendeeStatusChanged,
        eventReminders: settings.eventReminders,
        membershipExpiryReminders: settings.membershipExpiryReminders,
        friendApproved: settings.friendApproved,
        friendRequest: settings.friendRequest,
        groupInvitation: settings.groupInvitation,
        groupJoinRequestApproved: settings.groupJoinRequestApproved,
        groupJoinRequestReceived: settings.groupJoinRequestReceived,
        groupPostRejected: settings.groupPostRejected,
        mediaComment: settings.mediaComment,
        messageNew: settings.messageNew,
        placeReview: settings.placeReview,
        postComment: settings.postComment,
        publicationReview: settings.publicationReview,
        vendorMarketItemReview: settings.vendorMarketItemReview,
        vendorMarketReview: settings.vendorMarketReview,
        reviewReply: settings.reviewReply,
      })
    }
  }, [
    settings?.commentReply,
    settings?.enableEmails,
    settings?.enableMarketingEmails,
    settings?.eventCancelled,
    settings?.eventDetailsUpdated,
    settings?.eventInvitation,
    settings?.eventJoinRequestApproved,
    settings?.eventAttendeeStatusChanged,
    settings?.eventReminders,
    settings?.membershipExpiryReminders,
    settings?.friendApproved,
    settings?.friendRequest,
    settings?.groupInvitation,
    settings?.groupJoinRequestApproved,
    settings?.groupJoinRequestReceived,
    settings?.groupPostRejected,
    settings?.mediaComment,
    settings?.messageNew,
    settings?.placeReview,
    settings?.postComment,
    settings?.publicationReview,
    settings?.vendorMarketItemReview,
    settings?.vendorMarketReview,
    settings?.reviewReply,
    formMethods,
    settings,
  ])

  const [updateSettings] = useMutation(UPDATE_SETTINGS_MUTATION, {
    onCompleted: () => {
      toast.success(t('Email Settings Updated'))
      handleRefetch()
      setShowSpinnerValue(false)
    },
    onError: (err) => {
      toast.error(err.message)
      setShowSpinnerValue(false)
    },
  })

  const onSubmit = (fieldData: UpdateSettingsEmailInput) => {
    if (!settings) return
    setShowSpinnerValue(true)

    updateSettings({
      variables: {
        profileId: settings.ownerId,
        input: fieldData,
      },
    })
  }

  const enableEmails = useWatch({ control: formMethods.control, name: 'enableEmails' })

  const settingsGroups = useMemo(
    (): Array<{
      title: string
      description?: string
      settings: { key: keyof UpdateSettingsEmailInput; title: string }[]
    }> => [
      {
        title: t('Marketing: Email On...'),
        settings: [
          {
            key: 'enableMarketingEmails' as const,
            title: t("Product updates and what's new"),
          },
        ],
      },
      {
        title: t('Membership: Email On...'),
        settings: [
          {
            key: 'membershipExpiryReminders' as const,
            title: t('Supporter & preview access ending (reminders before your role expires)'),
          },
        ],
      },
      {
        title: t('Messages: Email On...'),
        settings: [{ key: 'messageNew' as const, title: t('New Messages') }],
      },
      {
        title: t('Events: Email On...'),
        settings: [
          { key: 'eventInvitation' as const, title: t('Event Invitations') },
          { key: 'eventJoinRequestApproved' as const, title: t('Event Request Accepted') },
          { key: 'eventDetailsUpdated' as const, title: t('Event Key Details Updated') },
          { key: 'eventAttendeeStatusChanged' as const, title: t('Event Attendance Status Changes') },
          { key: 'eventCancelled' as const, title: t('Event Cancellation') },
        ],
      },
      {
        title: t('Friends: Email On...'),
        settings: [
          { key: 'friendRequest' as const, title: t('Friend Requests') },
          { key: 'friendApproved' as const, title: t('Friend Approvals') },
        ],
      },
      {
        title: t('Groups: Email On...'),
        settings: [
          { key: 'groupInvitation' as const, title: t('Group Invitations') },
          { key: 'groupJoinRequestApproved' as const, title: t('Group Join Request Approved') },
          { key: 'groupJoinRequestReceived' as const, title: t('Group Join Request Received') },
          { key: 'groupPostRejected' as const, title: t('Group Post Rejected') },
        ],
      },
      {
        title: t('Media & Posts: Email On...'),
        settings: [
          { key: 'mediaComment' as const, title: t('Media Comments') },
          { key: 'postComment' as const, title: t('Post Comments') },
        ],
      },
      {
        title: t('Reviews & Comments: Email On...'),
        settings: [
          { key: 'placeReview' as const, title: t('Place Reviews') },
          { key: 'publicationReview' as const, title: t('Publication Reviews') },
          { key: 'vendorMarketReview' as const, title: t('Vendor market reviews') },
          { key: 'vendorMarketItemReview' as const, title: t('Vendor market item reviews') },
          { key: 'reviewReply' as const, title: t('Review Replies') },
          { key: 'commentReply' as const, title: t('Comment Replies') },
        ],
      },
    ],
    [t]
  )

  const highPrioritySettings = useMemo(() => [{ key: 'eventReminders' as const, title: t('Event Reminders') }], [t])

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!settings) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="ml-[200px] pt-3 text-destructive">{t('No Data')}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex w-full flex-col">
      <Form className="w-full" formMethods={formMethods} onSubmit={onSubmit}>
        <div className="flex w-full flex-col items-stretch gap-4">
          <Card className="w-full">
            <CardHeader>
              <CardTitle>{t('Email Preferences')}</CardTitle>
              <Separator />
            </CardHeader>
            <CardContent>
              <div className="flex w-full items-start justify-between gap-4">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <p className="font-medium text-foreground">{t('Enable Emails')}</p>
                  <p className="text-muted-foreground text-sm">
                    {t(
                      'When disabled, only high priority emails, and account emails will be sent. You can turn off high priority emails below.'
                    )}
                  </p>
                </div>
                <SwitchFormField fieldProps={{ className: 'w-auto shrink-0' }} name="enableEmails" />
              </div>
            </CardContent>
          </Card>

          {enableEmails
            ? settingsGroups.map((group) => (
                <Card className="w-full" key={group.title}>
                  <CardHeader>
                    <CardTitle>{group.title}</CardTitle>
                    {group.description ? <p className="text-muted-foreground text-sm">{group.description}</p> : null}
                    <Separator />
                  </CardHeader>
                  <CardContent>
                    <div className="flex w-full flex-col">
                      {group.settings.map((setting, idx) => (
                        <div
                          className={cn(
                            'flex w-full items-center justify-between gap-4 rounded-md p-2',
                            idx % 2 === 1 && 'bg-muted/30'
                          )}
                          key={setting.key}
                        >
                          <span className="text-foreground">{setting.title}</span>
                          <SwitchFormField fieldProps={{ className: 'w-auto shrink-0' }} name={setting.key} />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            : null}

          <Card className="w-full">
            <CardHeader>
              <CardTitle>{t('High Priority Emails: Email On...')}</CardTitle>
              <Separator />
            </CardHeader>
            <CardContent>
              <div className="flex w-full flex-col">
                {highPrioritySettings.map((setting, idx) => (
                  <div
                    className={cn(
                      'flex w-full items-center justify-between gap-4 rounded-md p-2',
                      idx % 2 === 1 && 'bg-muted/40'
                    )}
                    key={setting.key}
                  >
                    <span className="text-foreground">{setting.title}</span>
                    <SwitchFormField fieldProps={{ className: 'w-auto shrink-0' }} name={setting.key} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-6 flex w-full justify-end gap-2">
          {isDirty ? (
            <Button onClick={() => formMethods.reset()} type="button" variant="outline">
              {t('Cancel')}
            </Button>
          ) : null}
          <Button disabled={!isDirty || showSpinner} type="submit" variant="default">
            {showSpinner ? <Spinner className="mr-2 size-4" /> : null}
            {t('Save')}
          </Button>
        </div>
      </Form>
    </div>
  )
}
